/**
 * LossQuickForm Component
 *
 * Sticky form for quick loss entry.
 * Supports both raw product losses and finished product losses.
 * @see Sprint 13 - Pertes
 * @see US-13.6 - UI Page Pertes
 * @see Sprint XX - Pertes de Produits Finis
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Plus, Package, ChefHat, Info, AlertTriangle } from 'lucide-react';
import { Button, Input, Select, SearchAutocomplete, Tooltip } from '@/components/ui';
import { DatePicker, type DateRange } from '@/components/ui';
import { useDebounce, useCurrency, useBackdatingLimit, useBrandContext } from '@/hooks';
import { formatCentimes, formatQuantity, getQuantityStep } from '@/lib/format';
import { useRawProducts } from '@/features/raw-products/hooks/useRawProducts';
import {
  useCreateLoss,
  usePosProductsWithRecipe,
  useFinishedProductLossPreview,
  useCreateFinishedProductLoss,
} from '../hooks/useLosses';
import {
  LossReason,
  LOSS_REASON_OPTIONS,
} from '@bstock/shared';
import type { RawProductWithRelations, LossType } from '@bstock/shared';
import type { PosProductWithRecipeOption } from '../services/losses.service';

interface RawProductOption {
  id: string;
  label: string;
  sublabel?: string;
  recipeUnit: {
    code: string;
    symbol: string;
  };
}

interface PosProductOption {
  id: string;
  label: string;
  sublabel?: string;
  ingredientsCount: number;
}

/**
 * Convert raw product to autocomplete option
 */
function toRawProductOption(product: RawProductWithRelations): RawProductOption {
  return {
    id: product.id,
    label: product.name,
    sublabel: `${product.sku} - ${product.category.name}`,
    recipeUnit: product.recipeUnit,
  };
}

/**
 * Convert PosProduct to autocomplete option
 */
function toPosProductOption(product: PosProductWithRecipeOption): PosProductOption {
  return {
    id: product.id,
    label: product.name,
    sublabel: product.sku ?? product.posId,
    ingredientsCount: product.ingredientsCount,
  };
}

export function LossQuickForm() {
  const currency = useCurrency();
  const { minDate } = useBackdatingLimit();
  const { isCentral } = useBrandContext();

  // Type of loss (RAW or FINISHED)
  const [lossType, setLossType] = useState<LossType>('RAW');

  // Raw product state
  const [selectedRawProduct, setSelectedRawProduct] = useState<RawProductOption | null>(null);
  const [rawSearchValue, setRawSearchValue] = useState('');

  // Finished product state
  const [selectedPosProduct, setSelectedPosProduct] = useState<PosProductOption | null>(null);
  const [posSearchValue, setPosSearchValue] = useState('');

  // Common state
  const [quantity, setQuantity] = useState('');
  const [eventDateRange, setEventDateRange] = useState<DateRange>({
    start: new Date(),
    end: null,
  });
  const [lossReason, setLossReason] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ quantity?: string; lossReason?: string; notes?: string }>({});

  const quantityInputRef = useRef<HTMLInputElement>(null);

  const debouncedRawSearch = useDebounce(rawSearchValue, 300);
  const debouncedPosSearch = useDebounce(posSearchValue, 300);

  // Mutations
  const createRawLoss = useCreateLoss();
  const createFinishedLoss = useCreateFinishedProductLoss();

  // Search raw products
  const { data: rawProductsData, isLoading: isSearchingRaw } = useRawProducts({
    search: debouncedRawSearch,
    limit: 10,
    isActive: true,
  });

  // Search PosProducts with recipes
  const { data: posProductsData, isLoading: isSearchingPos } = usePosProductsWithRecipe(
    debouncedPosSearch,
    lossType === 'FINISHED'
  );

  // Preview for finished product loss
  const parsedQuantity = parseInt(quantity, 10);
  const { data: preview, isLoading: isLoadingPreview } = useFinishedProductLossPreview(
    selectedPosProduct?.id ?? null,
    isNaN(parsedQuantity) || parsedQuantity <= 0 ? 0 : parsedQuantity
  );

  const rawProductOptions = rawProductsData?.data.map(toRawProductOption) ?? [];
  const posProductOptions = posProductsData?.map(toPosProductOption) ?? [];

  // Focus quantity input after product selection
  useEffect(() => {
    if (selectedRawProduct || selectedPosProduct) {
      quantityInputRef.current?.focus();
    }
  }, [selectedRawProduct, selectedPosProduct]);

  const resetForm = useCallback(() => {
    setSelectedRawProduct(null);
    setSelectedPosProduct(null);
    setRawSearchValue('');
    setPosSearchValue('');
    setQuantity('');
    setEventDateRange({ start: new Date(), end: null });
    setLossReason('');
    setNotes('');
    setErrors({});
  }, []);

  // Reset form when loss type changes
  useEffect(() => {
    resetForm();
  }, [lossType, resetForm]);

  const validate = useCallback(() => {
    const newErrors: typeof errors = {};

    if (lossType === 'RAW' || lossType === 'EXCEPTIONAL') {
      if (!quantity || parseFloat(quantity) <= 0) {
        newErrors.quantity = 'Quantite requise';
      }
    } else {
      // FINISHED: must be integer > 0
      const qty = parseInt(quantity, 10);
      if (isNaN(qty) || qty <= 0) {
        newErrors.quantity = 'Quantite entiere requise';
      }
    }

    if (!lossReason) {
      newErrors.lossReason = 'Motif requis';
    }

    // If lossReason is AUTRE, notes is required
    if (lossReason === LossReason.AUTRE && (!notes || notes.trim() === '')) {
      newErrors.notes = 'Commentaire obligatoire pour motif "Autre"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [lossType, quantity, lossReason, notes]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const eventDate = eventDateRange.start?.toISOString() ?? undefined;

      if (lossType === 'RAW' || lossType === 'EXCEPTIONAL') {
        if (!selectedRawProduct) return;

        await createRawLoss.mutateAsync({
          rawProductId: selectedRawProduct.id,
          quantity: parseFloat(quantity),
          lossReason: lossReason as LossReason,
          lossType,
          eventDate,
          notes: notes.trim() || undefined,
        });
      } else {
        if (!selectedPosProduct) return;

        await createFinishedLoss.mutateAsync({
          posProductId: selectedPosProduct.id,
          quantity: parseInt(quantity, 10),
          lossReason: lossReason as LossReason,
          eventDate,
          notes: notes.trim() || undefined,
        });
      }

      resetForm();
    } catch {
      // Error is handled by mutation
    }
  }, [lossType, selectedRawProduct, selectedPosProduct, quantity, eventDateRange, lossReason, notes, validate, createRawLoss, createFinishedLoss, resetForm]);

  // Build loss reason options for select
  const lossReasonSelectOptions = [
    { value: '', label: 'Selectionner un motif' },
    ...LOSS_REASON_OPTIONS.map(opt => ({
      value: opt.value,
      label: opt.label,
    })),
  ];

  const isSubmitting = createRawLoss.isPending || createFinishedLoss.isPending;
  const isRawType = lossType === 'RAW' || lossType === 'EXCEPTIONAL';
  const hasProduct = isRawType ? !!selectedRawProduct : !!selectedPosProduct;

  return (
    <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0 sticky top-0 z-20 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Plus className="w-5 h-5 text-primary-500" />
        <h2 className="text-sm font-semibold text-dark-500 uppercase tracking-wider">
          Saisie rapide
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Loss Type Selector */}
        <div className="flex items-center gap-4 mb-4">
          <span className="text-sm font-medium text-dark-500">Type de perte :</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLossType('RAW')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                lossType === 'RAW'
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
              }`}
            >
              <Package className="w-4 h-4" />
              <span className="text-sm font-medium">Produit brut</span>
            </button>
            {!isCentral && (
              <button
                type="button"
                onClick={() => setLossType('FINISHED')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  lossType === 'FINISHED'
                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                    : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
                }`}
              >
                <ChefHat className="w-4 h-4" />
                <span className="text-sm font-medium">Produit fini</span>
              </button>
            )}
            <Tooltip content="Produits imposes par l'enseigne proches de la DLC. Non imputable a la rentabilite du restaurant.">
              <button
                type="button"
                onClick={() => setLossType('EXCEPTIONAL')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  lossType === 'EXCEPTIONAL'
                    ? 'bg-warning-50 border-warning-300 text-warning-700'
                    : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
                }`}
              >
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Exceptionnelle</span>
              </button>
            </Tooltip>
          </div>
          {lossType === 'FINISHED' && (
            <Tooltip content="La perte d'un produit fini decremente automatiquement tous les ingredients de sa recette">
              <Info className="w-4 h-4 text-neutral-400" />
            </Tooltip>
          )}
        </div>

        <div className="grid grid-cols-12 gap-3">
          {/* Product Search */}
          <div className="col-span-3">
            {isRawType ? (
              <SearchAutocomplete
                label="Produit brut"
                placeholder="Rechercher..."
                value={selectedRawProduct}
                onChange={setSelectedRawProduct}
                options={rawProductOptions}
                isLoading={isSearchingRaw}
                searchValue={rawSearchValue}
                onSearchChange={setRawSearchValue}
                minSearchLength={2}
                emptyMessage="Aucun produit trouve"
                keepFocusOnSelect
                renderOption={(option) => (
                  <>
                    <div className="text-dark-500">{option.label}</div>
                    <div className="text-xs text-neutral-400">{option.sublabel}</div>
                  </>
                )}
              />
            ) : (
              <SearchAutocomplete
                label="Produit fini"
                placeholder="Rechercher..."
                value={selectedPosProduct}
                onChange={setSelectedPosProduct}
                options={posProductOptions}
                isLoading={isSearchingPos}
                searchValue={posSearchValue}
                onSearchChange={setPosSearchValue}
                minSearchLength={2}
                emptyMessage="Aucun produit avec recette"
                keepFocusOnSelect
                renderOption={(option) => (
                  <>
                    <div className="text-dark-500">{option.label}</div>
                    <div className="text-xs text-neutral-400">
                      {option.sublabel} - {option.ingredientsCount} ingredients
                    </div>
                  </>
                )}
              />
            )}
          </div>

          {/* Quantity */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-dark-500 mb-1.5">
              Quantite <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <Input
                ref={quantityInputRef}
                type="number"
                step={getQuantityStep()}
                min="0"
                placeholder={isRawType ? '0.00' : '0'}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="text-lg font-semibold pr-12"
                disabled={!hasProduct}
              />
              {isRawType && selectedRawProduct && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">
                  {selectedRawProduct.recipeUnit.symbol}
                </span>
              )}
              {lossType === 'FINISHED' && selectedPosProduct && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">
                  unites
                </span>
              )}
            </div>
            {errors.quantity && (
              <p className="mt-1 text-xs text-danger-500">{errors.quantity}</p>
            )}
          </div>

          {/* Event Date */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-dark-500 mb-1.5">
              Date de la perte
            </label>
            <DatePicker
              asSingle
              showTime
              minDate={minDate}
              maxDate={new Date()}
              value={eventDateRange}
              onChange={setEventDateRange}
              placeholder="Aujourd'hui"
            />
          </div>

          {/* Loss Reason */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-dark-500 mb-1.5">
              Motif <span className="text-danger-500">*</span>
            </label>
            <Select
              value={lossReason}
              onChange={setLossReason}
              options={lossReasonSelectOptions}
              disabled={!hasProduct}
              allowEmpty={false}
            />
            {errors.lossReason && (
              <p className="mt-1 text-xs text-danger-500">{errors.lossReason}</p>
            )}
          </div>

          {/* Notes */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-dark-500 mb-1.5">
              Commentaire {lossReason === LossReason.AUTRE && <span className="text-danger-500">*</span>}
            </label>
            <Input
              type="text"
              placeholder="Optionnel..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!hasProduct}
            />
            {errors.notes && (
              <p className="mt-1 text-xs text-danger-500">{errors.notes}</p>
            )}
          </div>

          {/* Submit */}
          <div className="col-span-1 flex items-end">
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={!hasProduct || isSubmitting}
              isLoading={isSubmitting}
            >
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Preview for finished product loss */}
        {lossType === 'FINISHED' && selectedPosProduct && parsedQuantity > 0 && (
          <div className="mt-4 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
            <div className="flex items-center gap-2 mb-2">
              <ChefHat className="w-4 h-4 text-neutral-500" />
              <span className="text-sm font-medium text-dark-500">
                Recapitulatif des decrements
              </span>
              {isLoadingPreview && (
                <span className="text-xs text-neutral-400">Chargement...</span>
              )}
            </div>

            {preview && (
              <>
                <div className="grid grid-cols-4 gap-2 text-xs text-neutral-500 font-medium mb-1 px-2">
                  <span>Ingredient</span>
                  <span className="text-right">Qte/unite</span>
                  <span className="text-right">Qte totale</span>
                  <span className="text-right">Valorisation</span>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {preview.ingredients.map((ing) => (
                    <div
                      key={ing.rawProductId}
                      className="grid grid-cols-4 gap-2 text-sm px-2 py-1 bg-white rounded"
                    >
                      <span className="text-dark-500 truncate" title={ing.rawProductName}>
                        {ing.rawProductName}
                      </span>
                      <span className="text-right text-neutral-500">
                        {formatQuantity(ing.quantityPerUnit)} {ing.unitSymbol}
                      </span>
                      <span className="text-right font-medium text-dark-500">
                        {formatQuantity(ing.totalQuantity)} {ing.unitSymbol}
                      </span>
                      <span className="text-right text-danger-600">
                        {formatCentimes(ing.totalValueCentimes, currency)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-neutral-200 flex justify-between items-center">
                  <span className="text-sm text-dark-500">
                    {preview.ingredients.length} ingredients a decrementer
                  </span>
                  <span className="text-sm font-semibold text-danger-600">
                    Valorisation totale: {formatCentimes(preview.totalValueCentimes, currency)}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
